import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject, Inject, Optional } from '@angular/core';
import { IProductConfigurableSelection, IProductDataV2, IProductDataV3, Product } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { Client } from '@df/core/service/client';
import { Events } from '@df/core/service/event';
import { Gtm } from '@df/module-gtm/gtm.namespace';
import { GtmService } from '@df/module-gtm/gtm.service';
import { optionLabelPipe } from '@df/ui/common/option-label.pipe';
import { DfModal } from '@df/ui/modal/df-modal.decorator';
import { NDfModal } from '@df/ui/modal/df-modal.interface';
import { ModalRef } from '@df/ui/modal/modal-ref';
import { MODAL_DATA } from '@df/ui/modal/modal.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { LocalProduct } from 'projects/app-default/src/app/catalog/model/product/product.decorator';
import { ModalLayoutRightComponent, NModalLayoutRight } from 'projects/app-default/src/app/ui/modal/modal-layout-right.component';
import { NOutfitBuilder } from '../../../outfit-builder/outfit-builder.namespace';
import { NBuilderConfig } from '../../builder.config';
import { NBuilderProductsSetup } from '../builder-products-setup.config';
import { BuilderProductPreviewComponent } from './product/builder-product-preview.component';
export namespace NBuilderProductPreviewModal {
    /**
     * Modal identifier
     */
    export const id = 'builder-product-preview-modal';
    /**
     * Datapasset to modal
     */
    export interface IModalData {
        product: LocalProduct<IProductDataV3>;
        storageData: NOutfitBuilder.IStorageData;
        useGA4?: boolean;
    }
}
@DfModal({
    id: NBuilderProductPreviewModal.id,
    name: 'Outfit Product Preview Popup',
    position: 'right',
    reopenPolicy: NDfModal.ReopenPolicy.first,
    config: sizeName => ({
        ...NModalLayoutRight.ModalConfig,
        width: { S: '100vw', M: '100vw', L: '480px', X: '480px' }[sizeName],
        minWidth: { S: '100vw', M: '100vw' }[sizeName],
        height: '100%'
    }),
    sizeRestriction: sizeName => ({ S: ['S', 'M'], M: ['S', 'M'], L: ['L', 'X'], X: ['L', 'X'] })[sizeName]
})
@Component({
    selector: 'builder-product-preview-modal',
    templateUrl: './builder-product-preview-modal.html',
    standalone: true,
    imports: [ModalLayoutRightComponent, TranslationModule, BuilderProductPreviewComponent],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class BuilderProductPreviewModalComponent implements NDfModal.IModalComponent {
    /**
     * Modal option config
     */
    modalConfig!: NDfModal.IModalConfig;
    protected currentSelection?: IProductConfigurableSelection;
    protected matchedProduct?: Product<IProductDataV2>;
    private previousSelection?: IProductConfigurableSelection;
    private app = inject(App);
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private events: Events,
        private gtmService: GtmService,
        protected client: Client,
        @Optional() protected modalRef: ModalRef<any>,
        @Optional() @Inject(MODAL_DATA) public data: NBuilderProductPreviewModal.IModalData
    ) {}
    /**
     * Close modal
     */
    protected close(): void {
        this.modalRef.close();
    }
    protected onSelectionChange(selection: IProductConfigurableSelection): void {
        this.sendSizebayGtmEvent(selection);
        this.currentSelection = selection;
        const product = this.data.product;
        this.matchedProduct = product.matchChild(selection) as Product<IProductDataV2>;
        this.changeDetectorRef.detectChanges();
    }
    protected confirm(): void {
        if (!this.data.storageData.selection) {
            return;
        }
        const selectionExist = this.data.storageData.selection[this.data.storageData.currentSectionOptionId || ''];
        this.data.storageData.selection[this.data.storageData.currentSectionOptionId || ''] = {
            configurableSelection: this.currentSelection,
            productId: this.matchedProduct?.parent?.id || this.matchedProduct?.id || this.data.product.id,
            matchedProductId: this.matchedProduct?.id || this.data.product.id,
            productType: this.data.storageData.optionToggleState
        };
        // Instead click directive use directly service cause of conditions after storageData change
        // send list of options with seleceted products
        if (this.data.useGA4 && !selectionExist) {
            const data = this.getDataGA4();
            this.gtmService.sendEventGA4(data.name, data.details);
        }
        this.events.broadcast(NBuilderConfig.updateStorageEvent);
        this.close();
    }
    private getDataGA4(): Gtm.IGA4TrackData {
        const toggleState = this.data.storageData.optionToggleState;
        return {
            name: 'ob_product_types_selected',
            details: {
                entityType: toggleState,
                productTypes:
                    this.data.storageData.selection &&
                    Object.entries(this.data.storageData.selection)
                        .filter(([key, value]) => value.productType === toggleState)
                        .map(([key, value]) => optionLabelPipe(Number(key), NBuilderProductsSetup.SECTION_OPTION_ATTR_ID))
                        .join(',')
            }
        };
    }
    get currentSelectionData(): IProductConfigurableSelection | undefined {
        const currentSelection = this.data.storageData.selection?.[this.data.storageData.currentSectionOptionId || ''];
        return currentSelection?.productId === this.data.product.id ? currentSelection.configurableSelection : undefined;
    }

    get canConfirm(): boolean {
        return !this.data.product.isConfigurable || !!this.matchedProduct?.isSalable();
    }

    get displaySelectSize(): boolean {
        return !this.canConfirm && !!this.data?.product?.configurableAttributeCodes?.includes('size') && !this.currentSelection?.['size'];
    }

    private sendSizebayGtmEvent(selection: IProductConfigurableSelection) {
        if (this.previousSelection?.['color'] !== selection['color']) {
            const eventData = {
                event: 'product_view_sizebay',
                parent_sku: this.data.product.parent?.data?.sku,
                variant_sku: this.data.product.data?.sku !== this.data.product.parent?.data?.sku ? this.data.product.data?.sku : null,
                size: selection['size'] || null,
                permalink: this.app.url.storeUrl + this.data.product.data?.sku
            } as any;
            this.gtmService.push(eventData);
            this.previousSelection = { ...selection };
        }
    }
}
